import { mixed, number, object, ObjectSchema, string, ValidationError } from 'yup';
import { AnyObject, AssertsShape, ObjectShape, TypeOfShape } from 'yup/lib/object';
import {
  IMS_VALIDATION_PROPERTIES,
  IValidationProperties,
  TRANSLATION_KEYS,
} from '../types/validation';
import dayjs from 'dayjs';
import { GENDER_CODE_CIAM } from '../types/registrationFlows';
import { debounce } from './debounce';
import { validateAddress } from './validation';
import { AddressServiceLanguage } from '../services/addressService';
import { TFunction } from 'i18next';
import {
  CountryConfig,
  RegistrationFlowField,
  RegistrationFlowOrchestration,
} from '../types/apiConfig';

interface RegistrationBuilder {
  addFirstName(required: boolean): RegistrationBuilder;

  addLastName(required: boolean): RegistrationBuilder;

  addBirthDay(required: boolean): RegistrationBuilder;

  addGender(required: boolean): RegistrationBuilder;

  addAddress(
    orchestration: RegistrationFlowOrchestration,
    countryConfig: CountryConfig
  ): RegistrationBuilder;

  addTelephone(required: boolean, errorMessage?: string): RegistrationBuilder;

  build(): ObjectSchema<ObjectShape>;

  withConfig(
    orchestration: RegistrationFlowOrchestration,
    countryConfig: CountryConfig
  ): RegistrationBuilder;
}

export class RegistrationSchemaBuilder implements RegistrationBuilder {
  private baseObject: ObjectSchema<ObjectShape>;
  private readonly validationProps: IValidationProperties;
  private readonly countryIso: string | undefined;
  private readonly t: TFunction;
  private readonly addressServiceLanguage: AddressServiceLanguage;

  constructor(
    validationProps: IValidationProperties,
    countryIso: string | undefined,
    addressServiceLanguage: AddressServiceLanguage,
    t: TFunction
  ) {
    this.addressServiceLanguage = addressServiceLanguage;
    this.t = t;
    this.baseObject = object();
    this.validationProps = validationProps;
    this.countryIso = countryIso;
  }

  addFirstName(required = true): RegistrationBuilder {
    let schema = string()
      .max(this.validationProps.maxFirstNameLength, TRANSLATION_KEYS.INVALID_FIRSTNAME_FORMAT)
      .matches(
        new RegExp(this.validationProps.unicodeTextRegex, 'u'),
        TRANSLATION_KEYS.INVALID_FIRSTNAME_FORMAT
      );
    if (required) schema = schema.required(TRANSLATION_KEYS.REQUIRED_FIRSTNAME);
    this.baseObject = this.baseObject.shape({
      firstName: schema,
    });
    return this;
  }

  addLastName(required = true): RegistrationBuilder {
    let schema = string()
      .max(this.validationProps.maxLastNameLength, TRANSLATION_KEYS.INVALID_LASTNAME_FORMAT)
      .matches(
        new RegExp(this.validationProps.unicodeTextRegex, 'u'),
        TRANSLATION_KEYS.INVALID_LASTNAME_FORMAT
      );
    if (required) schema = schema.required(TRANSLATION_KEYS.REQUIRED_LASTNAME);
    this.baseObject = this.baseObject.shape({
      lastName: schema,
    });
    return this;
  }

  addBirthDay(required = true): RegistrationBuilder {
    let birthDateDaySchema = number()
      .min(1)
      .max(31)
      .test(function (item) {
        return !item || (this.parent.birthDateYear && this.parent.birthDateMonth);
      });
    let birthDateMonthSchema = number()
      .min(1)
      .max(12)
      .test(function (item) {
        return !item || (this.parent.birthDateYear && this.parent.birthDateDay);
      });
    let birthDateYearSchema = number()
      .min(this.validationProps.minBirthdateYear)
      .lessThan(this.getMaxBirthdateYear())
      .test(function (item) {
        return !item || (this.parent.birthDateDay && this.parent.birthDateMonth);
      });
    if (required) {
      birthDateDaySchema = birthDateDaySchema.required();
      birthDateMonthSchema = birthDateMonthSchema.required();
      birthDateYearSchema = birthDateYearSchema.required();
    }

    this.baseObject = this.baseObject.shape({
      birthDateDay: birthDateDaySchema,
      birthDateMonth: birthDateMonthSchema,
      birthDateYear: birthDateYearSchema,
    });
    return this;
  }

  addGender(required = true): RegistrationBuilder {
    let genderSchema = mixed().oneOf([Object.values(GENDER_CODE_CIAM), null]);
    if (required) genderSchema = genderSchema.required(TRANSLATION_KEYS.INVALID_GENDER);
    this.baseObject = this.baseObject.shape({
      gender: genderSchema,
      genderOther: string()
        .optional()
        .max(this.validationProps.maxGenderCustomLength, TRANSLATION_KEYS.INVALID_GENDER_CUSTOM),
    });
    return this;
  }

  addAddress(
    orchestration: RegistrationFlowOrchestration,
    countryConfig: CountryConfig
  ): RegistrationBuilder {
    let addressObject = object().test(
      debounce(
        (addressValues, ctx) =>
          this.validateAddress(
            {
              ...addressValues,
              country: ctx?.options?.context?.country,
            },
            this.addressServiceLanguage,
            countryConfig,
            orchestration
          ),
        300
      )
    );

    // Add country validation if it's in visible fields
    if (orchestration.visibleFields.includes(RegistrationFlowField.COUNTRY)) {
      let countrySchema = string();
      if (orchestration.mandatoryFields.includes(RegistrationFlowField.COUNTRY)) {
        countrySchema = countrySchema.required(TRANSLATION_KEYS.REQUIRED_COUNTRY_CODE);
      }
      this.baseObject = this.baseObject.shape({
        country: countrySchema,
      });
    }

    if (orchestration.visibleFields.includes(RegistrationFlowField.CITY)) {
      addressObject = addressObject.shape({
        city: string()
          .max(IMS_VALIDATION_PROPERTIES.MAX_CITY_LENGTH, TRANSLATION_KEYS.INVALID_CITY)
          .matches(IMS_VALIDATION_PROPERTIES.UNICODE_CITY_REGEX, TRANSLATION_KEYS.INVALID_CITY),
      });
    }
    if (orchestration.visibleFields.includes(RegistrationFlowField.POST_CODE)) {
      addressObject = addressObject.shape({
        postalCode: string()
          .max(
            IMS_VALIDATION_PROPERTIES.MAX_POSTAL_CODE_LENGTH,
            TRANSLATION_KEYS.INVALID_POSTAL_CODE
          )
          .matches(
            IMS_VALIDATION_PROPERTIES.UNICODE_POSTAL_CODE_REGEX,
            TRANSLATION_KEYS.INVALID_POSTAL_CODE
          ),
      });
    }
    if (orchestration.visibleFields.includes(RegistrationFlowField.STREET)) {
      addressObject = addressObject.shape({
        street: string()
          .required('register.error.requiredStreet')
          .max(IMS_VALIDATION_PROPERTIES.MAX_STREET_LENGTH, TRANSLATION_KEYS.INVALID_STREET)
          .matches(IMS_VALIDATION_PROPERTIES.UNICODE_STREET_REGEX, TRANSLATION_KEYS.INVALID_STREET),
      });
    }
    if (orchestration.visibleFields.includes(RegistrationFlowField.HOUSE_NUMBER)) {
      let houseNumberSchema = string()
        .max(
          IMS_VALIDATION_PROPERTIES.MAX_HOUSE_NUMBER_LENGTH,
          TRANSLATION_KEYS.INVALID_HOUSE_NUMBER
        )
        .matches(
          IMS_VALIDATION_PROPERTIES.UNICODE_HOUSE_NUMBER_REGEX,
          TRANSLATION_KEYS.INVALID_HOUSE_NUMBER
        );
      if (orchestration.mandatoryFields.includes(RegistrationFlowField.HOUSE_NUMBER))
        houseNumberSchema = houseNumberSchema.required('register.error.requiredHouseNumber');
      addressObject = addressObject.shape({
        houseNumber: houseNumberSchema,
      });
    }
    if (orchestration.visibleFields.includes(RegistrationFlowField.BOX_NUMBER)) {
      const errorTranslationKey = TRANSLATION_KEYS.INVALID_BOX_FORMAT;
      addressObject = addressObject.shape({
        box: string()
          .optional()
          .max(IMS_VALIDATION_PROPERTIES.BOX_NUMBER_LENGTH, errorTranslationKey)
          .matches(IMS_VALIDATION_PROPERTIES.BOX_NUMBER_REGEX, errorTranslationKey),
      });
    }
    this.baseObject = this.baseObject.shape({
      address: addressObject,
    });
    return this;
  }

  addTelephone(
    required = true,
    errorMessage: string = TRANSLATION_KEYS.INVALID_PHONENUMER_FORMAT
  ): RegistrationBuilder {
    let telephoneSchema = string().matches(
      new RegExp(this.validationProps.phoneRegex),
      errorMessage
    );
    telephoneSchema = required
      ? telephoneSchema.required('register.error.requiredTelephone')
      : telephoneSchema.optional();
    this.baseObject = this.baseObject.shape({ telephone: telephoneSchema });
    return this;
  }

  build(): ObjectSchema<
    ObjectShape,
    AnyObject,
    TypeOfShape<ObjectShape>,
    AssertsShape<ObjectShape>
  > {
    return this.baseObject;
  }

  withConfig(
    orchestration: RegistrationFlowOrchestration,
    countryConfig: CountryConfig
  ): RegistrationBuilder {
    if (orchestration.visibleFields.includes(RegistrationFlowField.FIRST_NAME))
      this.addFirstName(orchestration.mandatoryFields.includes(RegistrationFlowField.FIRST_NAME));

    if (orchestration.visibleFields.includes(RegistrationFlowField.LAST_NAME))
      this.addLastName(orchestration.mandatoryFields.includes(RegistrationFlowField.LAST_NAME));

    if (orchestration.visibleFields.includes(RegistrationFlowField.GENDER))
      this.addGender(orchestration.mandatoryFields.includes(RegistrationFlowField.GENDER));

    if (orchestration.visibleFields.includes(RegistrationFlowField.BIRTH_DATE))
      this.addBirthDay(orchestration.mandatoryFields.includes(RegistrationFlowField.BIRTH_DATE));

    if (orchestration.visibleFields.includes(RegistrationFlowField.PHONE_NUMBER))
      this.addTelephone(
        orchestration.mandatoryFields.includes(RegistrationFlowField.PHONE_NUMBER),
        this.t('register.telephoneHelp', {
          phoneNumberFormat: countryConfig.phoneNumberFormat,
        }).toString()
      );

    if (this.isAddressValidationRequired(orchestration))
      this.addAddress(orchestration, countryConfig);

    return this;
  }

  private isAddressValidationRequired(config: RegistrationFlowOrchestration): boolean {
    return [
      RegistrationFlowField.POST_CODE,
      RegistrationFlowField.STREET,
      RegistrationFlowField.HOUSE_NUMBER,
      RegistrationFlowField.BOX_NUMBER,
      RegistrationFlowField.CITY,
      RegistrationFlowField.COUNTRY,
    ].some((f) => config.mandatoryFields.includes(f));
  }

  private getMaxBirthdateYear() {
    return dayjs().get('year') - this.validationProps.maxBirthdateYearDifferenceFromToday;
  }

  private async validateAddress(
    values: {
      country?: string;
      city?: string;
      postalCode?: string;
      street?: string;
      houseNumber?: string;
    },
    ui_locales: AddressServiceLanguage,
    countryConfig: CountryConfig,
    orchestration: RegistrationFlowOrchestration
  ): Promise<boolean | ValidationError> {
    const validationResult = await validateAddress({
      ui_locales,
      address: {
        city: values.city,
        postalCode: values.postalCode,
        street: values.street,
        houseNumber: values.houseNumber,
        country: values.country,
      },
      countryConfig,
    });

    const validationErrors: ValidationError[] = [];
    if (
      orchestration.mandatoryFields.includes(RegistrationFlowField.COUNTRY) &&
      !validationResult.isValidCountry
    ) {
      validationErrors.push(
        new ValidationError(TRANSLATION_KEYS.REQUIRED_COUNTRY_CODE, undefined, 'country')
      );
    }

    // Checks if values are filled in & valid
    if (
      values.city &&
      values.postalCode &&
      countryConfig.validation.validatePostcode &&
      countryConfig.validation.validateCity &&
      !validationResult.isValidPostalCodeOrCity
    ) {
      validationErrors.push(
        new ValidationError('register.error.noMatchingPostalCodeOrCity', undefined, 'address.city')
      );
    }
    if (
      values.city &&
      !validationResult.isValidCity &&
      countryConfig.validation.validateCity &&
      orchestration.mandatoryFields.includes(RegistrationFlowField.CITY)
    ) {
      validationErrors.push(
        new ValidationError('register.error.invalidCity', undefined, 'address.city')
      );
    }
    if (
      values.postalCode &&
      !validationResult.isValidPostalCode &&
      countryConfig.validation.validatePostcode
    ) {
      validationErrors.push(
        new ValidationError('register.error.invalidPostalCode', undefined, 'address.postalCode')
      );
    }
    if (
      values.city &&
      values.postalCode &&
      countryConfig.validation.matchPostcodeAndCity &&
      orchestration.mandatoryFields.includes(RegistrationFlowField.CITY) &&
      !validationResult.isValidMatchPostalCodeCity
    ) {
      validationErrors.push(
        new ValidationError(
          'register.error.invalidMatchPostalCodeAddress',
          undefined,
          'address.city'
        )
      );
    }
    // Checks if fields are required
    if (orchestration.mandatoryFields.includes(RegistrationFlowField.CITY) && !values.city) {
      validationErrors.push(
        new ValidationError('register.error.requiredCity', undefined, 'address.city')
      );
    }
    if (
      orchestration.mandatoryFields.includes(RegistrationFlowField.POST_CODE) &&
      !values.postalCode
    ) {
      validationErrors.push(
        new ValidationError('register.error.requiredPostalCode', undefined, 'address.postalCode')
      );
    }

    const shouldCheckStreet =
      orchestration.visibleFields.includes(RegistrationFlowField.STREET) &&
      validationResult.isValidPostalCode;
    const inValidStreet = shouldCheckStreet && !validationResult.isValidStreet;
    if (inValidStreet && countryConfig.resolveDutchAddress) {
      validationErrors.push(
        new ValidationError(
          'register.error.invalidPostcodeAndHouseNumberCombo',
          undefined,
          'address.houseNumber'
        )
      );
    }
    if (inValidStreet) {
      validationErrors.push(
        new ValidationError('register.error.invalidStreet', undefined, 'address.street')
      );
    }
    const shouldCheckHouseNumber = shouldCheckStreet && values.houseNumber;
    const invalidHouseNumber = shouldCheckHouseNumber && !validationResult.isValidHouseNumber;
    if (countryConfig.validation.validateHouseNumber && invalidHouseNumber) {
      validationErrors.push(
        new ValidationError('register.error.invalidHouseNumber', undefined, 'address.houseNumber')
      );
    }
    return validationErrors.length > 0 ? new ValidationError(validationErrors) : true;
  }
}
